import React, { useState, useEffect, useRef } from 'react';
import "./ContactForm.css";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: 'Sales Effectiveness Consultancy',
    message: ''
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const sectionRef = useRef(null);

  const serviceOptions = [
    'Sales Effectiveness Consultancy',
    'Business Intelligence & Data Analytics',
    'Change Management and Capability Adoption'
  ];

  const environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
  
  const getApiUrl = () => {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://vzp7af7rpb.execute-api.us-east-1.amazonaws.com/prod';
    return `${baseUrl}/send-email`;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

const validatePhone = (phone) => {
  const phoneRegex = /^[0-9+\-\s\(\)\#]+$/;
  return phoneRegex.test(phone) && phone.replace(/[^0-9]/g, '').length >= 10;
};
  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const phoneRegex = /^[0-9+\-\s\(\)]*$/;
      if (!phoneRegex.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleServiceSelect = (service) => {
    setFormData(prev => ({
      ...prev,
      service: service
    }));
    setIsDropdownOpen(false);
    
    // Clear service error if it exists
    if (errors.service) {
      setErrors(prev => ({
        ...prev,
        service: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setSubmitError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const requestBody = {
        name: formData.name.trim(),
        phoneNumber: formData.phone.trim(),
        email: formData.email.trim(),
        selectService: formData.service,
        message: formData.message.trim(),
        environment: environment
      };

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        console.log('Form submitted successfully:', requestBody);
        setIsSubmitted(true);
        
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: 'Sales Effectiveness Consultancy',
          message: ''
        });
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        const errorData = await response.json();
        setSubmitError(errorData.message || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contact-section" id="contact-section" ref={sectionRef}>
      <div className="contact-container">
        <div className="contact-info">
          <div className="contact-header">CONTACT US</div>
          <h2 className="contact-title">Get In <span>Touch</span></h2>
          <p className="contact-description">
            Schedule a free 30 minutes consultation today to learn how we can help you grow your sales.
          </p>

          <div className="email-section">
            <div className="email-header">
              <div className="email-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <h3 className="email-title">Email Address</h3>
            </div>
            <div className="email-address">zuheir.shahwan@salestology.com</div>
          </div>

          <div className="follow-section">
            <h3>Follow Us:</h3>
            <a href="#" className="social-icon">
              <i className="fab fa-linkedin-in"></i>
            </a>
          </div>
        </div>

        <div className={`form-container ${isVisible ? 'animate-in' : ''}`}>
          <h2 className="form-title">Send <span>Message</span></h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <div className="form-label">Name *</div>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
                {errors.name && <div className="error-message">{errors.name}</div>}
              </div>
              <div className="form-group">
                <div className="form-label">Phone *</div>
                <input
                  type="tel"
                  name="phone"
                  className={`form-input ${errors.phone ? 'error' : ''}`}
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                {errors.phone && <div className="error-message">{errors.phone}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <div className="form-label">Email *</div>
                <input
                  type="email"
                  name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>
              <div className="form-group">
                <div className="form-label">Select Service *</div>
                <div className="dropdown-container">
                  <div 
                    className={`dropdown-trigger ${isDropdownOpen ? 'active' : ''} ${errors.service ? 'error' : ''}`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span>{formData.service}</span>
                    <i className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</i>
                  </div>
                  <div className={`dropdown-menu ${isDropdownOpen ? 'open' : ''}`}>
                    {serviceOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`dropdown-option ${formData.service === option ? 'selected' : ''}`}
                        onClick={() => handleServiceSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                </div>
                {errors.service && <div className="error-message">{errors.service}</div>}
              </div>
            </div>

            <div className="form-group">
              <div className="form-label">Message *</div>
              <textarea
                name="message"
                className={`form-textarea ${errors.message ? 'error' : ''}`}
                placeholder="Message"
                value={formData.message}
                onChange={handleInputChange}
              ></textarea>
              {errors.message && <div className="error-message">{errors.message}</div>}
            </div>

            <button 
              type="submit" 
              className="submit-button" 
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Make an Appointment'}
            </button>

            {/* Error Message */}
            {submitError && (
              <div className="error-message submit-error">
                {submitError}
              </div>
            )}

            {/* Success Message */}
            {isSubmitted && (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <span className="success-text">The form was sent successfully.</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;